import { useState, useEffect, useCallback, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Avatar, AvatarImage, AvatarFallback } from "../components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  Plus, Trash2, UserPlus, Users, Mail,
  TrendingUp, Check, X, Briefcase, Home, Heart, Clock, UserCheck, UserX, GraduationCap,
  Share2, ExternalLink, Link2, BookOpen,
  Dumbbell, Handshake, Brain, HelpCircle, Pencil, LayoutGrid, GitBranch, Sparkles
} from "lucide-react";
import SocialShareGrid from "../components/share/SocialShareGrid";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { trackInviteSent, trackConnectionAccepted } from "../utils/analytics";
import NetworkMindMap from "../components/NetworkMindMap";
import NetworkEmptyState from "../components/NetworkEmptyState";
import ConnectionDrawer from "../components/ConnectionDrawer";
import { LinkedName, LinkedAvatar } from "../components/LinkedProfile";
import InsightRequestDialog from "../components/InsightRequestDialog";
import CircleSnapshot from "../components/CircleSnapshot";
import MilestoneBadges from "../components/MilestoneBadges";
import InviteSuggestionsBanner from "../components/InviteSuggestionsBanner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

const API = '/api';

const NetworkPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [network, setNetwork] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: "", email: "", role: "" });
  const [sending, setSending] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [shareData, setShareData] = useState(null); // { inviteLink, name, role }
  const [generatingLink, setGeneratingLink] = useState(false);
  const [editingRole, setEditingRole] = useState(null); // connection_id being edited
  const [viewMode, setViewMode] = useState("mindmap"); // "mindmap" | "list"
  const [mindmapStats, setMindmapStats] = useState(null); // { active, pending, insights_this_week }
  const [drawerNode, setDrawerNode] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [insightDialogOpen, setInsightDialogOpen] = useState(false);
  const [snapshotOpen, setSnapshotOpen] = useState(false);
  const [removeConfirm, setRemoveConfirm] = useState(null); // connection object awaiting list-view delete confirm
  const [quotaStatus, setQuotaStatus] = useState(null); // {plan, used, limit, remaining, at_limit, waiting_quota_block}
  const mindmapSnapshotRef = useRef(null);

  // Profile share URL — uses the OG-enabled alias `/api/share/{username}`
  // so messengers (WhatsApp, iMessage, Slack, LinkedIn) crawl the rich
  // constellation card with description. Real browsers auto-redirect from
  // that path to the SPA at `/{username}`. Falls back to null when the
  // user hasn't reserved a username yet.
  const profileShareUrl = user?.username
    ? `${typeof window !== "undefined" ? window.location.origin : ""}/api/share/${user.username}?invite=1`
    : null;
  const firstName = (user?.name || "").split(" ")[0] || "I";
  const profileShareMessage = profileShareUrl
    ? t("network.shareIntentMessage", {
        firstName,
        url: profileShareUrl,
        defaultValue: `${firstName} invited you to join their Inner Circle on Evoluras. Join here: ${profileShareUrl}`,
      })
    : "";
  const profileShareSubject = t("network.shareEmailSubject", {
    firstName: firstName || "Someone",
    defaultValue: `${firstName || "Someone"} invited you to their Inner Circle on Evoluras`,
  });

  // Auto-open invite dialog when navigating with ?invite=true
  useEffect(() => {
    if (searchParams.get("invite") === "true") {
      setInviteDialogOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const fetchNetwork = useCallback(async () => {
    try {
      const response = await fetch(`${API}/network`, { credentials: 'include' });
      if (response.ok) {
        setNetwork(await response.json());
      }
    } catch (error) {
      toast.error('Failed to load network');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchQuota = useCallback(async () => {
    try {
      const r = await fetch(`${API}/network/invite-status`, { credentials: 'include' });
      if (r.ok) setQuotaStatus(await r.json());
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => {
    fetchNetwork();
    fetchQuota();
  }, [fetchNetwork, fetchQuota]);

  const sendInvite = async () => {
    if (!inviteForm.name || !inviteForm.email || !inviteForm.role) {
      toast.error('Please fill in all fields');
      return;
    }

    setSending(true);
    try {
      const response = await fetch(`${API}/network/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(inviteForm)
      });

      if (response.ok) {
        const result = await response.json();
        const inviteLink = `${window.location.origin}/invite/${result.invite_token}`;
        
        if (result.email_sent) {
          toast.success(`Email sent to ${inviteForm.name}!`);
        } else if (result.email_note) {
          toast.success('Connection added! Share the invite link below.');
        } else {
          toast.success('Connection added!');
        }
        
        // Show share dialog
        setShareData({ inviteLink, name: inviteForm.name, role: inviteForm.role });
        setInviteDialogOpen(false);
        trackInviteSent("network_page");
        setInviteForm({ name: "", email: "", role: "" });
        fetchNetwork();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send invitation');
      }
    } catch (error) {
      toast.error('Failed to send invitation');
    } finally {
      setSending(false);
    }
  };

  const removeConnection = async (connectionId) => {
    try {
      const response = await fetch(`${API}/network/${connectionId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setNetwork(prev => prev.filter(c => c.connection_id !== connectionId));
        toast.success('Connection removed');
        fetchQuota();
      } else {
        toast.error('Failed to remove connection');
      }
    } catch (error) {
      toast.error('Failed to remove connection');
    }
  };

  const acceptConnection = async (connectionId) => {
    setActionLoading(connectionId);
    try {
      const response = await fetch(`${API}/network/${connectionId}/accept`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        const result = await response.json();
        const accepted = network.find(c => c.connection_id === connectionId);
        if (result.status === 'pending_quota_block') {
          setNetwork(prev => prev.map(c =>
            c.connection_id === connectionId ? { ...c, status: 'pending_quota_block' } : c
          ));
          toast.warning(result.message || t('network.quotaBlockedToast'));
        } else {
          setNetwork(prev => prev.map(c =>
            c.connection_id === connectionId ? { ...c, status: 'accepted' } : c
          ));
          const handle = accepted?.display_username ? ` (@${accepted.display_username})` : '';
          const name = accepted?.name ? ` ${accepted.name.split(' ')[0]}${handle}` : '';
          toast.success(`Connection accepted!${name ? ` —${name}` : ''}`);
          trackConnectionAccepted();
        }
        fetchQuota();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to accept');
      }
    } catch (error) {
      toast.error('Failed to accept connection');
    } finally {
      setActionLoading(null);
    }
  };

  const declineConnection = async (connectionId) => {
    setActionLoading(connectionId);
    try {
      const response = await fetch(`${API}/network/${connectionId}/decline`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        setNetwork(prev => prev.filter(c => c.connection_id !== connectionId));
        toast.success('Connection declined');
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to decline');
      }
    } catch (error) {
      toast.error('Failed to decline connection');
    } finally {
      setActionLoading(null);
    }
  };

  const updateRole = async (connectionId, newRole) => {
    try {
      const response = await fetch(`${API}/network/${connectionId}/role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ role: newRole })
      });
      if (response.ok) {
        setNetwork(prev => prev.map(c => 
          c.connection_id === connectionId 
            ? { ...c, role: newRole, display_role: newRole } 
            : c
        ));
        setEditingRole(null);
        toast.success('Relationship updated');
      } else {
        const err = await response.json();
        toast.error(err.detail || 'Failed to update');
      }
    } catch (error) {
      toast.error('Failed to update relationship');
    }
  };

  const shareMessage = shareData
    ? `Hey ${shareData.name}! I'd love your insights on my personal development journey. Join me on Evoluras: ${shareData.inviteLink}`
    : '';

  const generateShareLink = async () => {
    setGeneratingLink(true);
    try {
      const response = await fetch(`${API}/network/invite-link`, {
        method: 'POST',
        credentials: 'include'
      });
      if (response.ok) {
        const result = await response.json();
        // Use the OG-enabled alias so messenger / WhatsApp / iMessage previews
        // render the inviter's profile constellation card. Real browsers
        // auto-redirect from /api/share/invite/{token} to /invite/{token}.
        const inviteLink = `${window.location.origin}/api/share/invite/${result.invite_token}`;
        setShareData({ inviteLink, name: 'your contact', role: '' });
        setInviteDialogOpen(false);
      } else {
        toast.error('Failed to generate link');
      }
    } catch (error) {
      toast.error('Failed to generate link');
    } finally {
      setGeneratingLink(false);
    }
  };

  const getRoleIcon = (role) => {
    const icons = {
      mentor: Users,
      mentee: GraduationCap,
      professor: BookOpen,
      student: GraduationCap,
      coworker: Briefcase,
      coach: Dumbbell,
      coachee: Dumbbell,
      business_partner: Handshake,
      therapist: Brain,
      family: Home,
      friend: Heart,
      other: HelpCircle
    };
    return icons[role] || Users;
  };

  const getRoleColor = (role) => {
    const colors = {
      mentor: 'bg-purple-100 text-purple-600',
      mentee: 'bg-indigo-100 text-indigo-600',
      professor: 'bg-teal-100 text-teal-600',
      student: 'bg-cyan-100 text-cyan-600',
      coworker: 'bg-violet-100 text-violet-600',
      coach: 'bg-orange-100 text-orange-600',
      coachee: 'bg-orange-100 text-orange-600',
      business_partner: 'bg-blue-100 text-blue-600',
      therapist: 'bg-pink-100 text-pink-600',
      family: 'bg-green-100 text-green-600',
      friend: 'bg-amber-100 text-amber-600',
      other: 'bg-slate-100 text-slate-500'
    };
    return colors[role] || 'bg-slate-100 text-slate-600';
  };

  const groupedNetwork = {
    mentor: network.filter(c => (c.display_role || c.role) === 'mentor'),
    mentee: network.filter(c => (c.display_role || c.role) === 'mentee'),
    professor: network.filter(c => (c.display_role || c.role) === 'professor'),
    student: network.filter(c => (c.display_role || c.role) === 'student'),
    coworker: network.filter(c => (c.display_role || c.role) === 'coworker'),
    coach: network.filter(c => ['coach', 'coachee'].includes(c.display_role || c.role)),
    business_partner: network.filter(c => (c.display_role || c.role) === 'business_partner'),
    therapist: network.filter(c => (c.display_role || c.role) === 'therapist'),
    family: network.filter(c => (c.display_role || c.role) === 'family'),
    friend: network.filter(c => (c.display_role || c.role) === 'friend'),
    other: network.filter(c => (c.display_role || c.role) === 'other')
  };

  const pendingIncoming = network.filter(c => c.direction === 'incoming' && c.status === 'pending');
  const activeConnections = network.filter(c => !(c.direction === 'incoming' && c.status === 'pending'));

  const formatRole = (role) => {
    const labels = { business_partner: 'Business Partner', other: 'Other' };
    return labels[role] || (role?.charAt(0).toUpperCase() + role?.slice(1));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-2 border-violet-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="network-page">
      {/* Quota banner — surfaces when basic-plan circle is full or has stuck invites */}
      {quotaStatus && quotaStatus.limit !== null &&
        (quotaStatus.at_limit || (quotaStatus.waiting_quota_block || 0) > 0) && (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <div
            className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3"
            data-testid="quota-banner"
          >
            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Users className="w-4 h-4 text-amber-700" />
            </div>
            <div className="flex-1 text-sm text-amber-900">
              <div className="font-semibold">
                {t('network.quotaBannerTitle', { used: quotaStatus.used, limit: quotaStatus.limit })}
              </div>
              <div className="text-amber-800 mt-0.5">
                {(quotaStatus.waiting_quota_block || 0) > 0
                  ? t('network.quotaBannerWaiting', { count: quotaStatus.waiting_quota_block })
                  : t('network.quotaBannerAtLimit')}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Page Title */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 
              className="text-2xl font-bold text-slate-900"
              style={{ fontFamily: 'system-ui, sans-serif' }}
            >
              {t("network.titleMyCircle")}
            </h1>
            {network.length > 0 ? (
              <div className="flex items-center gap-3 mt-1.5 flex-wrap" data-testid="circle-stats-ticker">
                <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-100">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                  <span className="text-xs font-medium text-emerald-700">
                    {t("network.activeCount", { count: mindmapStats?.active ?? network.filter(c => c.status === 'accepted').length })}
                  </span>
                </div>
                {(mindmapStats?.pending ?? pendingIncoming.length) > 0 && (
                  <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-100">
                    <Clock className="w-3 h-3 text-amber-600" />
                    <span className="text-xs font-medium text-amber-700">
                      {t("network.pendingCount", { count: mindmapStats?.pending ?? pendingIncoming.length })}
                    </span>
                  </div>
                )}
                <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-violet-50 border border-violet-100">
                  <Sparkles className="w-3 h-3 text-violet-600" />
                  <span className="text-xs font-medium text-violet-700">
                    {t("network.insightsThisWeek", { count: mindmapStats?.insights_this_week ?? 0 })}
                  </span>
                </div>
                {quotaStatus && quotaStatus.limit !== null && (
                  <div
                    className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border ${
                      quotaStatus.at_limit
                        ? 'bg-rose-50 border-rose-200 text-rose-700'
                        : 'bg-slate-50 border-slate-200 text-slate-600'
                    }`}
                    data-testid="plan-quota-chip"
                    title={t('network.planQuotaTooltip', { plan: quotaStatus.plan })}
                  >
                    <span className="text-xs font-medium capitalize">
                      {quotaStatus.plan}
                    </span>
                    <span className="text-xs font-semibold">
                      {quotaStatus.used}/{quotaStatus.limit}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-slate-500 mt-1">{t("network.startsWithOneVoice")}</p>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {network.filter(c => c.status === 'accepted').length > 0 && (
              <Button
                onClick={() => setSnapshotOpen(true)}
                variant="outline"
                className="rounded-full border-violet-200 text-violet-700 hover:bg-violet-50 hover:border-violet-300"
                data-testid="share-circle-button"
              >
                <Share2 className="w-4 h-4 mr-2" />
                {t("common.share")}
              </Button>
            )}
            <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-violet-600 hover:bg-violet-700 text-white rounded-full"
                  data-testid="invite-button"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  {t("network.invite")}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: 'system-ui, sans-serif' }}>
                    {t("network.inviteDialogQuestion")}
                  </DialogTitle>
                </DialogHeader>
                
                <Tabs defaultValue="share" className="mt-2">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="share" data-testid="tab-share-link">
                      <Link2 className="w-4 h-4 mr-1.5" />
                      {t("network.shareLink")}
                    </TabsTrigger>
                    <TabsTrigger value="email" data-testid="tab-email-invite">
                      <Mail className="w-4 h-4 mr-1.5" />
                      {t("network.emailInvite")}
                    </TabsTrigger>
                  </TabsList>

                  {/* Share Link Tab — uses the user's public profile URL,
                      no token generation step. Visitors land on /{username}
                      and can request a connection via the existing
                      role-picker dialog. */}
                  <TabsContent value="share" className="space-y-4 mt-4">
                    <p className="text-sm text-slate-600">
                      {t("network.inviteDialogBody")}
                    </p>

                    {profileShareUrl ? (
                      <SocialShareGrid
                        url={profileShareUrl}
                        message={profileShareMessage}
                        shortText={firstName}
                        emailSubject={profileShareSubject}
                        nativeTitle={profileShareSubject}
                        testIdPrefix="share"
                      />
                    ) : (
                      // No username yet — funnel to handle reservation first.
                      <div
                        className="rounded-lg p-4 text-sm space-y-2"
                        style={{
                          background: "rgba(255,255,255,0.04)",
                          border: "1px solid rgba(255,255,255,0.10)",
                          color: "var(--color-text-secondary)",
                        }}
                      >
                        <p>{t("network.shareNeedHandle")}</p>
                        <Link
                          to="/profile"
                          onClick={() => setInviteDialogOpen(false)}
                          className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium"
                          style={{
                            background: "var(--color-brand-primary)",
                            color: "var(--color-text-on-brand)",
                          }}
                          data-testid="reserve-handle-link"
                        >
                          {t("network.shareReserveHandle")}
                        </Link>
                      </div>
                    )}
                  </TabsContent>

                  {/* Email Invite Tab */}
                  <TabsContent value="email" className="space-y-4 mt-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Name</label>
                      <Input
                        placeholder="John Doe"
                        value={inviteForm.name}
                        onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                        data-testid="invite-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                      <Input
                        type="email"
                        placeholder="john@example.com"
                        value={inviteForm.email}
                        onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                        data-testid="invite-email-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Relationship</label>
                      <Select 
                        value={inviteForm.role} 
                        onValueChange={(v) => setInviteForm({ ...inviteForm, role: v })}
                      >
                        <SelectTrigger data-testid="invite-role-select">
                          <SelectValue placeholder="Select relationship" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="mentor">Mentor</SelectItem>
                          <SelectItem value="mentee">Mentee</SelectItem>
                          <SelectItem value="professor">Professor</SelectItem>
                          <SelectItem value="student">Student</SelectItem>
                          <SelectItem value="coworker">Coworker</SelectItem>
                          <SelectItem value="coach">Coach</SelectItem>
                          <SelectItem value="coachee">Coachee</SelectItem>
                          <SelectItem value="business_partner">Business Partner</SelectItem>
                          <SelectItem value="therapist">Therapist</SelectItem>
                          <SelectItem value="family">Family</SelectItem>
                          <SelectItem value="friend">Friend</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button 
                      onClick={sendInvite}
                      disabled={sending}
                      className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full"
                      data-testid="send-invite-button"
                    >
                      {sending ? 'Sending...' : 'Ask for their honest take'}
                      <Mail className="w-4 h-4 ml-2" />
                    </Button>
                  </TabsContent>
                </Tabs>
              </DialogContent>
            </Dialog>

            {/* Share Dialog */}
            <Dialog open={!!shareData} onOpenChange={(open) => !open && setShareData(null)}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: 'system-ui, sans-serif' }}>
                    <div className="flex items-center gap-2">
                      <Share2 className="w-5 h-5 text-violet-600" />
                      Share Invite
                    </div>
                  </DialogTitle>
                </DialogHeader>
                <div className="py-4 space-y-5">
                  <p className="text-sm text-slate-600">
                    Share this invite link with <span className="font-medium text-slate-900">{shareData?.name}</span> so they can join your network.
                  </p>

                  <SocialShareGrid
                    url={shareData?.inviteLink || ""}
                    message={shareMessage}
                    shortText={shareData?.name || "Evoluras"}
                    emailSubject={`An invite for ${shareData?.name || "you"} on Evoluras`}
                    nativeTitle="Evoluras invite"
                    testIdPrefix="invite-share"
                  />

                  <Button
                    onClick={() => setShareData(null)}
                    variant="outline"
                    className="w-full rounded-full"
                    data-testid="close-share-dialog"
                  >
                    Done
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        {/* Milestone badges row (only when there's any network activity) */}
        {network.length > 0 && (
          <div className="mb-4" data-testid="milestones-row">
            <MilestoneBadges refreshKey={network.length} />
          </div>
        )}

        {/* Context-aware invite suggestions (recent SWOT items without insights) */}
        {network.length > 0 && (
          <InviteSuggestionsBanner
            onInvite={() => setInviteDialogOpen(true)}
            onAsk={() => setInsightDialogOpen(true)}
          />
        )}

        {/* View Toggle */}
        {network.length > 0 && (
          <div className="flex items-center gap-1 mb-6 bg-slate-100 rounded-full p-1 w-fit" data-testid="view-mode-toggle">
            <button
              onClick={() => setViewMode("mindmap")}
              className={`relative flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                viewMode === "mindmap"
                  ? "bg-white text-violet-700 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
              data-testid="view-mode-mindmap"
            >
              <GitBranch className="w-3.5 h-3.5" />
              Mind Map
              {pendingIncoming.length > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-amber-500 ring-2 ring-white"
                  data-testid="mindmap-pending-dot"
                  title={`${pendingIncoming.length} pending request${pendingIncoming.length === 1 ? "" : "s"}`}
                />
              )}
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`relative flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                viewMode === "list"
                  ? "bg-white text-violet-700 shadow-sm"
                  : "text-slate-500 hover:text-slate-700"
              }`}
              data-testid="view-mode-list"
            >
              <LayoutGrid className="w-3.5 h-3.5" />
              List
              {pendingIncoming.length > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-amber-500 ring-2 ring-white"
                  data-testid="list-pending-dot"
                  title={`${pendingIncoming.length} pending request${pendingIncoming.length === 1 ? "" : "s"}`}
                />
              )}
            </button>
          </div>
        )}

        {/* Mind Map View */}
        {viewMode === "mindmap" && network.length > 0 && (
          <NetworkMindMap
            snapshotRef={mindmapSnapshotRef}
            onNodeClick={(node) => {
              setDrawerNode(node);
              setDrawerOpen(true);
            }}
            onStatsChange={setMindmapStats}
          />
        )}

        {/* List View */}
        {viewMode === "list" && network.length > 0 && (
          <div className="space-y-8">
            {/* Pending Incoming Requests */}
            {pendingIncoming.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-amber-100 text-amber-600">
                    <Clock className="w-4 h-4" />
                  </div>
                  <h2 
                    className="text-lg font-semibold text-slate-900"
                    style={{ fontFamily: 'system-ui, sans-serif' }}
                  >
                    Pending Requests
                  </h2>
                  <span className="text-sm text-slate-500">({pendingIncoming.length})</span>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {pendingIncoming.map((conn) => {
                    const RoleIcon = getRoleIcon(conn.role);
                    return (
                      <div 
                        key={conn.connection_id}
                        className="card p-4 border-amber-200 bg-amber-50/30"
                        data-testid={`pending-connection-${conn.connection_id}`}
                      >
                        <div className="flex items-center gap-4">
                          <LinkedAvatar username={conn.display_username} className="flex-shrink-0">
                            <Avatar className="h-12 w-12">
                              {conn.display_picture ? (
                                <AvatarImage src={conn.display_picture} alt={conn.inviter_name || conn.name} />
                              ) : null}
                              <AvatarFallback className={getRoleColor(conn.role)}>
                                {(conn.inviter_name || conn.name)?.charAt(0) || '?'}
                              </AvatarFallback>
                            </Avatar>
                          </LinkedAvatar>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-slate-900 truncate">
                              <LinkedName username={conn.display_username} name={conn.inviter_name || conn.name}
                                testId={`invite-name-${conn.connection_id}`}>
                                {conn.inviter_name || conn.name}
                              </LinkedName>
                            </h3>
                            {conn.display_username ? (
                              <Link
                                to={`/${conn.display_username}`}
                                className="text-xs text-slate-500 hover:text-violet-600 truncate block"
                                onClick={(e) => e.stopPropagation()}
                                data-testid={`invite-handle-${conn.connection_id}`}
                              >
                                @{conn.display_username}
                              </Link>
                            ) : null}
                            <p className="text-sm text-slate-500">Wants to add you as their <span className="font-medium">{formatRole(conn.role)}</span></p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-3 ml-16">
                          <Button
                            size="sm"
                            onClick={() => acceptConnection(conn.connection_id)}
                            disabled={actionLoading === conn.connection_id}
                            className="bg-violet-600 hover:bg-violet-700 text-white rounded-full px-4"
                            data-testid={`accept-btn-${conn.connection_id}`}
                          >
                            <UserCheck className="w-4 h-4 mr-1" />
                            {actionLoading === conn.connection_id ? '...' : 'Accept'}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => declineConnection(conn.connection_id)}
                            disabled={actionLoading === conn.connection_id}
                            className="rounded-full px-4"
                            data-testid={`decline-btn-${conn.connection_id}`}
                          >
                            <UserX className="w-4 h-4 mr-1" />
                            Decline
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Active connections grouped by role */}
            {Object.entries(groupedNetwork).map(([role, connections]) => {
              const activeInRole = connections.filter(c => !(c.direction === 'incoming' && c.status === 'pending'));
              if (activeInRole.length === 0) return null;
              
              const RoleIcon = getRoleIcon(role);
              
              return (
                <div key={role}>
                  <div className="flex items-center gap-2 mb-4">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getRoleColor(role)}`}>
                      <RoleIcon className="w-4 h-4" />
                    </div>
                    <h2 
                      className="text-lg font-semibold text-slate-900"
                      style={{ fontFamily: 'system-ui, sans-serif' }}
                    >
                      {formatRole(role)}s
                    </h2>
                    <span className="text-sm text-slate-500">({connections.length})</span>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {activeInRole.map((conn) => (
                      <div 
                        key={conn.connection_id}
                        className="card p-4 card-hover"
                        data-testid={`connection-${conn.connection_id}`}
                      >
                        <div className="flex items-center gap-4">
                          <LinkedAvatar username={conn.display_username} className="flex-shrink-0">
                            <Avatar className="h-12 w-12">
                              {conn.display_picture ? (
                                <AvatarImage src={conn.display_picture} alt={conn.display_name || conn.name} />
                              ) : null}
                              <AvatarFallback className={getRoleColor(conn.display_role || conn.role)}>
                                {(conn.display_name || conn.name)?.charAt(0) || '?'}
                              </AvatarFallback>
                            </Avatar>
                          </LinkedAvatar>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-slate-900 truncate">
                              <LinkedName username={conn.display_username} name={conn.display_name || conn.name}
                                testId={`conn-name-${conn.connection_id}`}>
                                {conn.display_name || conn.name}
                              </LinkedName>
                            </h3>
                            {conn.display_username ? (
                              <Link
                                to={`/${conn.display_username}`}
                                className="text-xs text-slate-500 hover:text-violet-600 truncate block"
                                onClick={(e) => e.stopPropagation()}
                                data-testid={`conn-handle-${conn.connection_id}`}
                              >
                                @{conn.display_username}
                              </Link>
                            ) : null}
                            <p className="text-sm text-slate-500 truncate capitalize">{conn.display_role || conn.role}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {editingRole === conn.connection_id ? (
                              <Select 
                                value={conn.display_role || conn.role}
                                onValueChange={(v) => updateRole(conn.connection_id, v)}
                              >
                                <SelectTrigger className="w-[140px] h-8 text-xs" data-testid={`edit-role-select-${conn.connection_id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="mentor">Mentor</SelectItem>
                                  <SelectItem value="mentee">Mentee</SelectItem>
                                  <SelectItem value="professor">Professor</SelectItem>
                                  <SelectItem value="student">Student</SelectItem>
                                  <SelectItem value="coworker">Coworker</SelectItem>
                                  <SelectItem value="coach">Coach</SelectItem>
                                  <SelectItem value="coachee">Coachee</SelectItem>
                                  <SelectItem value="business_partner">Business Partner</SelectItem>
                                  <SelectItem value="therapist">Therapist</SelectItem>
                                  <SelectItem value="family">Family</SelectItem>
                                  <SelectItem value="friend">Friend</SelectItem>
                                  <SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              <button
                                onClick={() => setEditingRole(conn.connection_id)}
                                className={`badge ${
                                  conn.status === 'accepted'
                                    ? 'bg-green-100 text-green-700'
                                    : conn.status === 'pending_quota_block'
                                      ? 'bg-rose-100 text-rose-700'
                                      : 'bg-yellow-100 text-yellow-700'
                                } cursor-pointer hover:ring-2 hover:ring-violet-300 transition-all`}
                                title={conn.status === 'pending_quota_block'
                                  ? t('network.quotaBlockedRowTooltip')
                                  : 'Click to change relationship'}
                                data-testid={`edit-role-btn-${conn.connection_id}`}
                              >
                                {conn.status === 'accepted' ? (
                                  <><Check className="w-3 h-3 mr-1" /> {formatRole(conn.display_role || conn.role)}</>
                                ) : conn.status === 'pending_quota_block' ? (
                                  <>{t('network.waitingForUpgrade')}</>
                                ) : (
                                  'Pending'
                                )}
                                <Pencil className="w-3 h-3 ml-1.5 opacity-50" />
                              </button>
                            )}
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => setRemoveConfirm(conn)}
                              className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50"
                              data-testid={`remove-connection-${conn.connection_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Empty State */}
        {network.length === 0 && (
          <NetworkEmptyState onInviteClick={() => setInviteDialogOpen(true)} />
        )}

        {/* Connection Drawer — opens on mindmap node click */}
        <ConnectionDrawer
          node={drawerNode}
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          onRoleChange={() => { fetchNetwork(); }}
          onRemove={() => { fetchNetwork(); }}
          onAskInsight={(n) => {
            setDrawerNode(n);
            setInsightDialogOpen(true);
          }}
        />

        {/* Insight Request Dialog — triggered from drawer */}
        <InsightRequestDialog
          open={insightDialogOpen}
          onOpenChange={setInsightDialogOpen}
          network={network}
          toolType="swot"
          toolLabel="Personal SWOT"
          showToolPicker={true}
          onRequestSent={() => { setInsightDialogOpen(false); fetchNetwork(); }}
        />

        {/* Circle Snapshot — download/share the mind map as PNG */}
        <CircleSnapshot
          open={snapshotOpen}
          onOpenChange={setSnapshotOpen}
          targetRef={mindmapSnapshotRef}
          userName={user?.name}
        />

        {/* List-view remove confirmation */}
        <AlertDialog open={!!removeConfirm} onOpenChange={(open) => !open && setRemoveConfirm(null)}>
          <AlertDialogContent data-testid="list-remove-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>
                Remove {removeConfirm?.display_name || removeConfirm?.name || "this person"} from your circle?
              </AlertDialogTitle>
              <AlertDialogDescription>
                You&apos;ll lose the ability to exchange insights. Any previous insights they&apos;ve shared will stay with you. This can&apos;t be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="list-remove-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  if (removeConfirm) {
                    removeConnection(removeConfirm.connection_id);
                  }
                  setRemoveConfirm(null);
                }}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="list-remove-confirm-btn"
              >
                Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default NetworkPage;
